import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import moment from 'moment';
import { db, auth } from '../lib/firebase';
import '../styles/Transfer.css';
import Loader from '../components/Loader';
import progress from '../images/progress.png';
import ChartInfo from '../components/ChartInfo';
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { sendTransactionEmail } from '../lib/Email';


const Transfer = ({ user }) => {
  const navigate = useNavigate();

  // ✅ New States for verification
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [inputCode, setInputCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [userCode, setUserCode] = useState("");

  const [bankName, setBankName] = useState('');
  const [beneficiary, setBeneficiary] = useState('');
  const [beneficiaryAccount, setBeneficiaryAccount] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(moment().format('YYYY-MM-DD'));
  const [account, setAccount] = useState('Savings');
  const [totalCredits, setTotalCredits] = useState(0);
  const [totalDebits, setTotalDebits] = useState(0);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [userBalance, setUserBalance] = useState(0);
  const [accountBalances, setAccountBalances] = useState({});
  const [loading, setLoading] = useState(true);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [userGroup, setUserGroup] = useState(null);
  const [userDP, setUserDP] = useState(null);
  const [userFullName, setUserFullName] = useState('');
  const [userAccountNumber, setUserAccountNumber] = useState('');
  const [userAmount, setUserAmount] = useState();
  const [userBankName, setUserBankName] = useState();
  const [userBeneficiary, setUserBeneficiary] = useState();
  const [userBeneficiaryAccount, setUserBeneficiaryAccount] = useState();
  const [userDescription, setUserDescription] = useState();
  const [userDate, setUserDate] = useState();

  const [transactionType, setTransactionType] = useState('');
  const [swiftCode, setSwiftCode] = useState('');
  const [routingNumber, setRoutingNumber] = useState('');
  const [country, setCountry] = useState('');
  const [bankAddress, setBankAddress] = useState('');
  const [userTransactionId, setUserTransactionId] = useState('');
  const [userEmail, setUserEmail] = useState('');



  useEffect(() => {
    if (user && user.uid) {
      const fetchUserData = async () => {
        try {
          const userDoc = await getDoc(doc(db, 'alpha', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const fetchedTransactions = userData.transactions || [];
            const accountBalances = {};

            const userAccountNumber = userData.accountNumber || '************';
            const userFullName = userData.fullName || 'No name';
            const userGroup = userData.group || 'No group available';
            const userDP = userData.profilePictureUrl || progress;

            // ✅ set saved verification code
            const savedCode = userData.code || "";
            setUserCode(savedCode);

            const credits = fetchedTransactions.reduce((total, txn) => txn.type === 'credit' ? total + txn.amount : total, 0);
            const debits = fetchedTransactions.reduce((total, txn) => txn.type === 'debit' ? total + txn.amount : total, 0);
            const totalBalance = credits - debits;

            fetchedTransactions.forEach((txn) => {
              if (!accountBalances[txn.account]) {
                accountBalances[txn.account] = 0;
              }
              accountBalances[txn.account] += txn.type === 'credit' ? txn.amount : -txn.amount;
            });

            setTotalCredits(credits);
            setTotalDebits(debits);
            setTotalTransactions(fetchedTransactions.length);
            setUserBalance(totalBalance);
            setAccountBalances(accountBalances);
            setTransactions(fetchedTransactions);
            setUserGroup(userGroup);
            setUserDP(userDP);
            setUserFullName(userFullName);
            setUserAccountNumber(userAccountNumber);
            setUserEmail(userData.email || user.email || '');

          }
        } catch (error) {
          console.error('Error fetching user data: ', error);
        } finally {
          setLoading(false);
        }
      };
      fetchUserData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const isInsufficientBalance = parseFloat(amount) > (accountBalances[account] || 0);

  const generateTransactionId = () => {
    return 'TXN-' + Math.random().toString(36).substring(2, 10).toUpperCase();
  };


  const handleSubmit = async (e) => {
    e?.preventDefault();
    setFormSubmitting(true);
    setSuccessMessage('');

    if (!bankName || !beneficiary || !beneficiaryAccount || !amount || !description || !account) {
      setFormSubmitting(false);
      return;
    }

    const parsedAmount = parseFloat(amount);
    if (accountBalances[account] <= 0 || parsedAmount > accountBalances[account]) {
      setFormSubmitting(false);
      return;
    }

    try {
      const userDocRef = doc(db, 'alpha', user.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) return;

      const txnId = generateTransactionId();

      const transaction = {
        bankName,
        beneficiary,
        beneficiaryAccount,
        amount: parsedAmount,
        description: description,
        date: moment(date).format('MMMM D, YYYY'),
        type: 'debit',
        account,
        transactionType,
        swiftCode,
        routingNumber,
        country,
        bankAddress,
        transactionId: txnId
      };


      await updateDoc(userDocRef, { transactions: arrayUnion(transaction) });

      // ✅ Call the email service
      await sendTransactionEmail(userEmail || user.email, userAccountNumber, userFullName, transaction);


      setUserBalance(userBalance - parsedAmount);
      setAccountBalances(prev => ({
        ...prev,
        [account]: prev[account] - parsedAmount
      }));

      setBankName('');
      setBeneficiary('');
      setBeneficiaryAccount('');
      setAmount('');
      setDescription('');
      setDate(moment().format('YYYY-MM-DD'));
      setAccount('Savings');
      setUserAmount(`$${amount}`);
      setUserBeneficiary(`${beneficiary}`);
      setUserBeneficiaryAccount(`${beneficiaryAccount}`);
      setUserBankName(`${bankName}`);
      setUserDescription(description);
      setUserDate(moment(date).format('MMMM D, YYYY'));
      setSuccessMessage(`The transfer of $${amount} was sent to ${beneficiary}.`);
      setUserTransactionId(txnId);
      setTransactionType('');
      setSwiftCode('');
      setRoutingNumber('');
      setCountry('');
      setBankAddress('');



    } catch (error) {
      console.error('Error adding transaction: ', error);
    } finally {
      setFormSubmitting(false);
    }
  };

  if (loading) return <div><Loader /></div>;

  return (
    <div className="add-transaction" id='transfer'>
      <div className="summary-boxed">
        <div className="summaryBoxes">Credits: ${totalCredits.toFixed(2)}</div>
        <div className="summaryBoxes">Debits: ${totalDebits.toFixed(2)}</div>
        <div className="summaryBoxes">Total Transactions: {totalTransactions}</div>
      </div>

      <div><ChartInfo transactions={transactions} /></div>

      <form onSubmit={handleSubmit}>
        <div className='form-cover'>
          <div>
            <label className='transaction-head'>Beneficiary:</label>
            <input type="text" value={beneficiary} placeholder='John Doe'
              className='input-field' onChange={(e) => setBeneficiary(e.target.value)} required />
          </div>

          <div>
            <label className='transaction-head'>Beneficiary Account:</label>
            <input type="text" value={beneficiaryAccount} className="input-field"
              placeholder="0123456789"
              onChange={(e) => { const v = e.target.value; if (/^[0-9]*$/.test(v)) setBeneficiaryAccount(v); }} required />
          </div>
        </div>

        <div className='form-cover'>
          <div>
            <label className='transaction-head'>Bank Name:</label>
            <input type="text" className='input-field' value={bankName}
              placeholder='Bank of America' onChange={(e) => setBankName(e.target.value)} required />
          </div>

          <div>
            <label className='transaction-head'>Amount:</label>
            <input type="number" value={amount} className='input-field'
              placeholder='example: 100' onChange={(e) => setAmount(e.target.value)}
              required min="0.01" step="0.01" />
          </div>
        </div>

        <div className='form-cover'>
          <div>
            <label className='transaction-head'>Description:</label>
            <input type="text" className='input-field' value={description}
              placeholder='Payment for Electricity' onChange={(e) => setDescription(e.target.value)} required />
          </div>
        </div>

        <div className='form-cover'>
          <div>
            <label className='transaction-head'>Transaction Type:</label>
            <select className='input-field' value={transactionType} required onChange={e => setTransactionType(e.target.value)}>
              <option value="">Select Type</option>
              <option value="Swift Transfer">SWIFT Transfer</option>
              <option value="International Transfer">International Transfer</option>
              <option value="Domestic Transfer">Domestic Transfer</option>
              <option value="Wire Transfer">Wire Transfer</option>
            </select>
          </div>

          <div>
            <label className='transaction-head'>Swift Code:</label>
            <input type="text" value={swiftCode} className='input-field'
              placeholder='BOFAUS3N' onChange={e => setSwiftCode(e.target.value)} required />
          </div>
        </div>

        <div className='form-cover'>
          <div>
            <label className='transaction-head'>Routing Number:</label>
            <input type="text" value={routingNumber} className='input-field'
              placeholder='123456789' onChange={e => setRoutingNumber(e.target.value)} required />
          </div>

          <div>
            <label className='transaction-head'>Country:</label>
            <input type="text" value={country} className='input-field'
              placeholder='United States' onChange={e => setCountry(e.target.value)} required />
          </div>
        </div>

        <div className='form-cover'>
          <div>
            <label className='transaction-head'>Bank Address:</label>
            <input type="text" value={bankAddress} className='input-field'
              placeholder='123 Wall Street, NY USA'
              onChange={e => setBankAddress(e.target.value)} required />
          </div>
        </div>


        <div className='submit'>
          {userGroup === 'cancelled' ? (
            <div className="cancelled-message">
              <p>
                Dear Customer, we have discovered suspicious activities on your account. An unauthorized IP address attempted to carry out a transaction on your account. Consequently, your account has been flagged by our risk assessment department. kindly visit our nearest branch with your identification card and utility bill to confirm your identity before it can be reactivated. for more information, kindly contact our online customer care
                representative or chat an agent using the Live Chat feature.
              </p>
            </div>
          ) : (
            <button
              type="button"
              className="contact-support-button"
              disabled={formSubmitting || isInsufficientBalance}
              onClick={() => {
                if (userGroup !== 'activated') {
                  setShowCodeModal(true);
                } else {
                  handleSubmit();
                }
              }}
            >
              {formSubmitting ? 'Processing...' : 'Transfer'}
            </button>
          )}
        </div>
      </form>

      {/* ✅ CODE VERIFICATION MODAL */}
      {showCodeModal && (
        <div className="verification-overlay">
          <div className="verification-modal">

            {/* Small top box like the screenshot */}
            <div className="tac-icon-box"></div>

            <h2 className="tac-title">Transfer Authorization Code is required.</h2>

            <p className="tac-text-warning">
              The International TAC code is required before this transaction can be completed
              successfully. You can visit any of our nearest branches or contact our online
              customer care representative for more details regarding your TAC code for this
              transaction.
            </p>

            <label className="tac-label">Enter Transfer Authorization Code:</label>

            <div className="tac-input-wrapper">
              <input
                type="password"
                maxLength="6"
                value={inputCode}
                onChange={(e) => setInputCode(e.target.value)}
                placeholder="******"
                className="input-field tac-input"
              />
              <span className="tac-suffix">TAC</span>
            </div>

            {codeError && <p className="error tac-error">{codeError}</p>}

            <p className="tac-footer-note">
              We have security measures in place to safeguard your money, because we are
              committed to providing you with a secure banking experience.
            </p>

            <button
              className="contact-support-button tac-submit"
              onClick={(e) => {
                if (inputCode === userCode) {
                  setShowCodeModal(false);
                  setCodeError("");
                  handleSubmit(e);
                } else {
                  setCodeError("Incorrect TAC — please try again.");
                }
              }}
            >
              Submit
            </button>

            <button
              className="closed tac-cancel"
              onClick={() => {
                setShowCodeModal(false);
                setFormSubmitting(false);
              }}
            >
              Cancel
            </button>

          </div>
        </div>
      )}

      {successMessage && (
        <div className="msg">
          <div className="msg-box">

            <h2 style={{ color: '#dedede' }}>Your transaction was successful!</h2>

            <p>Description: {userDescription}</p>
            <p>Transaction ID: {userTransactionId}</p>
            <p>Date: {userDate}</p>

            <div className="details-container">
              <div className="sender-details">
                <img src={userDP} alt="Sender" />
                <div>
                  <h4>{userFullName}</h4>
                  <p>Account: {userAccountNumber}</p>
                  <p>Transaction Amount: {userAmount}</p>
                </div>
              </div>

              <div className="connector-line"></div>

              <div className="beneficiary-details">
                <img src={progress} alt="Beneficiary" />
                <div>
                  <h4>{userBeneficiary}</h4>
                  <p>Beneficiary Bank: {userBankName}</p>
                  <p>Account Number: {userBeneficiaryAccount}</p>
                </div>
              </div>



              <div
                className="btn-submit"

              >
                <span
                  className='tac-cancel contact-support-button btn-submit'
                  onClick={() => setSuccessMessage('')}
                >
                  ← Go Back
                </span>

                <span
                  className="tac-submit contact-support-button jst-btn"
                  onClick={async () => {
                    await signOut(auth);  
                    navigate("/login");  
                  }}
                >
                  × Logout
                </span>
              </div>


            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default Transfer;